import { useState, useEffect, useCallback } from 'react';
import { KeyRound, Loader2, Save, CheckCircle, XCircle, AlertTriangle, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Input from '@/components/ui/Input';

/**
 * Super-admin management of PLATFORM-level credentials (platform_credentials,
 * migration 220). A value saved here is read by every tenant container, which
 * is what used to require the same env var on 23 containers plus four separate
 * plumbing edits.
 *
 * Distinct from /admin/settings/credentials (PlatformCredentials.tsx), which
 * writes the TENANT-level override table. Resolution order is
 * tenant override -> platform (this page) -> env, so a site that has set its
 * own value is never overridden from here.
 */

interface PlatformCredential {
  key: string;
  label: string;
  category: string;
  source: 'platform_db' | 'env' | 'none';
  masked_value: string | null;
  is_set: boolean;
  can_clear: boolean;
  updated_at: string | null;
}

const CATEGORY_LABELS: Record<string, string> = {
  search: 'Web Search, Scraping & Parsing',
  advertising: 'Advertising',
  payments: 'Payments',
  email: 'Email',
  ai: 'AI & Suppliers',
  auth: 'Authentication',
  storage: 'Storage',
  infrastructure: 'Infrastructure',
  pms: 'Property Management',
  media: 'Media & Stock Photos',
  integrations: 'Integrations (OAuth apps)',
};

// Search first: it is the category this surface was built for and the one that
// changes most often. The rest follow in the order the backend groups them.
const CATEGORY_ORDER = [
  'search', 'ai', 'media', 'advertising', 'integrations', 'payments', 'email',
  'pms', 'infrastructure', 'auth', 'storage',
];

export default function PlatformCredentialsSection() {
  const [credentials, setCredentials] = useState<PlatformCredential[]>([]);
  const [sharedPlatformDb, setSharedPlatformDb] = useState(true);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get<{ credentials: PlatformCredential[]; shared_platform_db: boolean }>(
      '/admin/platform/credentials',
    )
      .then((res) => {
        setCredentials(res.credentials || []);
        setSharedPlatformDb(res.shared_platform_db !== false);
      })
      .catch(() => setMessage('Failed to load platform credentials'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave(key: string) {
    const value = (drafts[key] || '').trim();
    if (!value) return;
    setBusyKey(key);
    setMessage('');
    try {
      await api.put(`/admin/platform/credentials/${key}`, { value });
      setDrafts((d) => ({ ...d, [key]: '' }));
      setMessage(`${key} saved — live on every site within a minute.`);
      load();
    } catch (err: any) {
      setMessage(err?.message || `Failed to save ${key}`);
    } finally {
      setBusyKey(null);
    }
  }

  async function handleClear(key: string) {
    if (!confirm(`Remove the platform-wide value for ${key}? Sites with their own override are unaffected; the rest fall back to the environment variable if one is set.`)) return;
    setBusyKey(key);
    setMessage('');
    try {
      await api.delete(`/admin/platform/credentials/${key}`);
      setMessage(`${key} cleared.`);
      load();
    } catch (err: any) {
      setMessage(err?.message || `Failed to clear ${key}`);
    } finally {
      setBusyKey(null);
    }
  }

  if (loading) {
    return (
      <Card className="p-5 mb-6">
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
      </Card>
    );
  }

  const grouped = CATEGORY_ORDER
    .map((cat) => ({
      category: cat,
      label: CATEGORY_LABELS[cat] || cat,
      items: credentials.filter((c) => c.category === cat),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <Card className="p-5 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <KeyRound className="w-5 h-5 text-sage-700" />
        <div>
          <h2 className="text-lg font-semibold text-stone-900">Platform Credentials</h2>
          <p className="text-sm text-stone-500">
            Shared API keys read by every site. A site that sets its own value in
            its Security settings still wins; anything unset here falls back to
            the environment variable.
          </p>
        </div>
      </div>

      {!sharedPlatformDb && (
        <div className="mb-4 p-3 rounded border border-amber-200 bg-amber-50 text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Platform DB not configured</p>
            <p className="text-amber-800">
              <code>PLATFORM_DATABASE_URL</code> is not set on this container, so
              values saved here apply only to the current tenant.
            </p>
          </div>
        </div>
      )}

      {message && (
        <p className={`mb-4 text-sm ${message.toLowerCase().includes('fail') ? 'text-red-600' : 'text-green-600'}`}>
          {message}
        </p>
      )}

      <div className="space-y-6">
        {grouped.map((group) => (
          <div key={group.category}>
            <h3 className="text-sm font-semibold text-stone-700 mb-2">{group.label}</h3>
            <div className="space-y-2">
              {group.items.map((cred) => (
                <div
                  key={cred.key}
                  className="flex flex-col md:flex-row md:items-center gap-2 p-3 rounded border border-stone-200"
                >
                  <div className="md:w-1/3">
                    <p className="text-sm font-medium text-stone-900">{cred.label}</p>
                    <code className="text-xs text-stone-500">{cred.key}</code>
                  </div>

                  <div className="md:w-1/4 flex items-center gap-1.5 text-xs">
                    {cred.is_set ? (
                      <>
                        <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                        <span className="text-stone-600">
                          {cred.source === 'platform_db' ? 'Platform' : 'Environment'}
                        </span>
                        {cred.masked_value && (
                          <code className="bg-stone-100 px-1.5 py-0.5 rounded">{cred.masked_value}</code>
                        )}
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3.5 h-3.5 text-stone-400" />
                        <span className="text-stone-500">Not set</span>
                      </>
                    )}
                  </div>

                  <div className="flex-1 flex items-center gap-2">
                    <Input
                      value={drafts[cred.key] || ''}
                      onChange={(e) => setDrafts((d) => ({ ...d, [cred.key]: e.target.value }))}
                      placeholder={cred.is_set ? 'Enter a new value to replace' : 'Enter value...'}
                      type="password"
                      className="flex-1"
                    />
                    <Button
                      onClick={() => handleSave(cred.key)}
                      disabled={busyKey === cred.key || !(drafts[cred.key] || '').trim()}
                      size="sm"
                    >
                      {busyKey === cred.key
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Save className="w-4 h-4" />}
                    </Button>
                    {cred.can_clear && (
                      <Button
                        onClick={() => handleClear(cred.key)}
                        disabled={busyKey === cred.key}
                        variant="outline"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
